import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { Project, ProjectVersion } from '@/shared/types'

export interface ProjectWithVersions extends Project {
  versions: ProjectVersion[]
}

async function fetchProjectWithVersions(
  shortId: string
): Promise<ProjectWithVersions | null> {
  // Fetch project by short_id
  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select('*')
    .eq('short_id', shortId)
    .single()

  if (projectError) {
    if (projectError.code === 'PGRST116') {
      return null
    }
    throw new Error(projectError.message)
  }

  // Fetch versions for this project
  const { data: versions, error: versionsError } = await supabase
    .from('project_versions')
    .select('*')
    .eq('project_id', project.id)
    .order('version_number', { ascending: false })

  if (versionsError) {
    throw new Error(versionsError.message)
  }

  return {
    ...(project as Project),
    versions: (versions || []) as ProjectVersion[],
  }
}

export function useProjectWithVersions(shortId: string | undefined) {
  return useQuery({
    queryKey: ['project', 'short', shortId, 'versions'],
    queryFn: () => fetchProjectWithVersions(shortId!),
    enabled: !!shortId,
  })
}
